import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const TermsPage = () => {
  const navigate = useNavigate();
  const lastUpdated = 'March 12, 2026';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-8 text-foreground/80 hover:text-foreground hover:bg-accent">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <h1 className="text-4xl font-bold text-foreground mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-12">Last updated: {lastUpdated}</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using SuperTool AI, you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">2. Platform Description</h2>
            <p className="text-muted-foreground leading-relaxed">
              SuperTool AI is an AI-powered application development platform that allows users to create web and mobile applications through natural language instructions. The platform provides AI code generation, deployment, and hosting services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">3. User Accounts</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>You must provide accurate and complete information when creating an account</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must be at least 13 years old to use the platform</li>
              <li>One person or entity may not maintain more than one account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">4. Credit System and Payments</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              SuperTool AI operates on a credit-based system:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Credits are consumed when you use AI features (code generation, chat, deployment)</li>
              <li>Unused credits from monthly subscriptions do not roll over</li>
              <li>Purchased credit packs do not expire</li>
              <li>All purchases are non-refundable unless required by law</li>
              <li>Prices may change with 30 days notice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">5. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              You retain full ownership of all code and content generated through the platform. SuperTool AI does not claim any intellectual property rights over your projects. You are free to use, modify, distribute, and commercialize any output from the platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">6. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">You agree not to use SuperTool AI to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Build applications that violate any applicable laws</li>
              <li>Generate malicious code, malware, or exploits</li>
              <li>Circumvent rate limits or abuse the credit system</li>
              <li>Attempt to reverse-engineer the platform or its AI models</li>
              <li>Engage in any activity that disrupts or degrades the service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">7. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              SuperTool AI is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages arising from your use of the platform. Our total liability shall not exceed the amount you have paid us in the preceding 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">8. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may suspend or terminate your account if you violate these terms. You may delete your account at any time through the Settings page. Upon termination, your project data will be retained for 30 days before permanent deletion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">9. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions about these Terms of Service, contact us at{' '}
              <a href="mailto:legal@supertool.ai" className="text-blue-400 hover:text-blue-300">legal@supertool.ai</a>.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsPage;
